import { useState, useEffect } from 'react';
import api from '@/api';
import { Loader2, HeartPulse, AlertTriangle, CheckCircle2, XCircle, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const GRADE_STYLES = {
  A: { bg: 'bg-[#57EB31]/10', border: 'border-[#57EB31]/40', text: 'text-[#57EB31]' },
  B: { bg: 'bg-[#3B82F6]/10', border: 'border-[#3B82F6]/40', text: 'text-[#3B82F6]' },
  C: { bg: 'bg-[#3B82F6]/10', border: 'border-[#3B82F6]/40', text: 'text-[#3B82F6]' },
  D: { bg: 'bg-orange-500/10', border: 'border-orange-500/40', text: 'text-orange-400' },
  F: { bg: 'bg-red-500/10', border: 'border-red-500/40', text: 'text-red-400' },
  'N/A': { bg: 'bg-[#2D3F5E]/20', border: 'border-[#2D3F5E]', text: 'text-[#5C6A7A]' },
};

const RISK_STYLES = {
  low: 'text-[#57EB31]',
  moderate: 'text-[#3B82F6]',
  elevated: 'text-[#3B82F6]',
  high: 'text-orange-400',
  critical: 'text-red-400',
  unknown: 'text-[#5C6A7A]',
};

export default function AccountHealth() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('score');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/ops-analytics/account-health');
        setAccounts(res.data || []);
      } catch { toast.error('Failed to load health scores'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const sorted = [...accounts].sort((a, b) => {
    const av = a[sortField], bv = b[sortField];
    const cmp = typeof av === 'number' ? av - bv : String(av || '').localeCompare(String(bv || ''));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  // Summary stats
  const gradeCount = { A: 0, B: 0, C: 0, D: 0, F: 0, 'N/A': 0 };
  accounts.forEach(a => { gradeCount[a.grade] = (gradeCount[a.grade] || 0) + 1; });

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="text-[#3B82F6] animate-spin" size={28} /></div>;

  return (
    <div className="p-6 animate-fade-in" data-testid="account-health-page">
      <div className="mb-6">
        <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#5C6A7A] mb-1">// FINANCE</p>
        <h1 className="text-white font-['Manrope'] font-extrabold text-2xl">Account Health Scores</h1>
        <p className="text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm mt-0.5">Payment behavior and risk assessment for each company</p>
      </div>

      {/* Grade Summary */}
      <div className="grid grid-cols-6 gap-3 mb-6" data-testid="grade-summary">
        {Object.entries(gradeCount).map(([grade, count]) => {
          const s = GRADE_STYLES[grade] || GRADE_STYLES['N/A'];
          return (
            <div key={grade} className={`${s.bg} border ${s.border} rounded-sm p-4 text-center`}>
              <p className={`font-['JetBrains_Mono'] text-3xl font-extrabold ${s.text}`}>{grade}</p>
              <p className="text-xs font-['IBM_Plex_Sans'] text-[#5C6A7A] mt-1">{count} {count === 1 ? 'company' : 'companies'}</p>
            </div>
          );
        })}
      </div>

      {/* Table */}
      <div className="bg-[#1A2332] border border-[#2D3F5E] rounded-sm overflow-hidden" data-testid="health-table">
        <div className="grid grid-cols-12 gap-2 px-4 py-2.5 bg-[#0A0F1A] border-b border-[#2D3F5E]">
          {[
            { label: 'Company', field: 'company_name', span: 3 },
            { label: 'Grade', field: 'grade', span: 1 },
            { label: 'Score', field: 'score', span: 1 },
            { label: 'Risk', field: 'risk_level', span: 1 },
            { label: 'Invoices', field: 'total_invoices', span: 1 },
            { label: 'Paid', field: 'paid', span: 1 },
            { label: 'Unpaid', field: 'unpaid', span: 1 },
            { label: 'Overdue', field: 'overdue', span: 1 },
            { label: 'Billed', field: 'total_billed', span: 1 },
            { label: 'Outstanding', field: 'total_outstanding', span: 1 },
          ].map(col => (
            <button key={col.field} onClick={() => toggleSort(col.field)}
              className={`col-span-${col.span} flex items-center gap-1 text-[9px] font-['JetBrains_Mono'] uppercase text-[#5C6A7A] hover:text-[#3B82F6] transition-colors`}>
              {col.label} {sortField === col.field && <ArrowUpDown size={9} />}
            </button>
          ))}
        </div>

        {sorted.length === 0 ? (
          <p className="text-center py-12 text-[#5C6A7A] font-['IBM_Plex_Sans'] text-sm">No customer data</p>
        ) : (
          <div className="divide-y divide-[#2D3F5E]/30">
            {sorted.map(a => {
              const gs = GRADE_STYLES[a.grade] || GRADE_STYLES['N/A'];
              const rs = RISK_STYLES[a.risk_level] || RISK_STYLES.unknown;
              return (
                <div key={a.customer_id} className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-[#0A0F1A]/60 transition-colors"
                  data-testid={`health-row-${a.customer_id}`}>
                  <div className="col-span-3 min-w-0">
                    <p className="text-xs font-['IBM_Plex_Sans'] font-medium text-white truncate">{a.company_name}</p>
                    <p className="text-[9px] font-['JetBrains_Mono'] text-[#5C6A7A]">{a.tenant_group}</p>
                  </div>
                  <div className="col-span-1">
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-sm ${gs.bg} border ${gs.border} font-['JetBrains_Mono'] font-extrabold text-sm ${gs.text}`}>
                      {a.grade}
                    </span>
                  </div>
                  <div className="col-span-1">
                    <div className="flex items-center gap-1">
                      <div className="w-10 h-1.5 rounded-full bg-[#0A0F1A] overflow-hidden">
                        <div className={`h-full rounded-full ${a.score >= 80 ? 'bg-[#57EB31]' : a.score >= 60 ? 'bg-[#3B82F6]' : a.score >= 40 ? 'bg-[#3B82F6]' : 'bg-red-500'}`}
                          style={{ width: `${a.score}%` }} />
                      </div>
                      <span className="text-xs font-['JetBrains_Mono'] font-bold text-white">{a.score}</span>
                    </div>
                  </div>
                  <div className="col-span-1">
                    <span className={`text-[10px] font-['JetBrains_Mono'] font-bold uppercase ${rs}`}>{a.risk_level}</span>
                  </div>
                  <div className="col-span-1">
                    <span className="text-xs font-['JetBrains_Mono'] text-[#8B9AAB]">{a.total_invoices}</span>
                  </div>
                  <div className="col-span-1">
                    <span className="text-xs font-['JetBrains_Mono'] text-[#57EB31]">{a.paid}</span>
                  </div>
                  <div className="col-span-1">
                    <span className="text-xs font-['JetBrains_Mono'] text-[#3B82F6]">{a.unpaid}</span>
                  </div>
                  <div className="col-span-1">
                    <span className={`text-xs font-['JetBrains_Mono'] ${a.overdue > 0 ? 'text-red-400 font-bold' : 'text-[#5C6A7A]'}`}>{a.overdue}</span>
                  </div>
                  <div className="col-span-1">
                    <span className="text-xs font-['JetBrains_Mono'] text-[#8B9AAB]">{fmt(a.total_billed)}</span>
                  </div>
                  <div className="col-span-1">
                    <span className={`text-xs font-['JetBrains_Mono'] ${a.total_outstanding > 0 ? 'text-red-400 font-bold' : 'text-[#5C6A7A]'}`}>
                      {fmt(a.total_outstanding)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
